import { FC, useRef, useState, useEffect } from 'react'
import { Button, Slider, Icon, OverlayTrigger, Tooltip, ActionButton } from 'ui'
import { useHistoryState, useHistoryActions } from '@hooks/useHistory'
import { useTranslations } from 'use-intl'
import { HTMLElementIdsForTracking } from '@constants/htmlElementIdsForTracking'
import styles from './HistoryDialog.module.scss'
import PlayIcon from '/public/s2_icon_play.svg'
import PauseIcon from '/public/s2_icon_pause.svg'

const animationStep = 0.005
const animationInterval = 16
// loop up beyond slider maximum to show the last frame a little longer so the finished product is emphasized
const animationMax = 1.3

export interface HistorySliderProps {
  isProjectViewOnly?: boolean
  isFeatured: boolean | null
  engineState: string
  handleOnButtonClick: () => void
  isCreatingProject: boolean
  isDuplicatingProject: boolean
  sliderRef: React.RefObject<HTMLDivElement>
  height: number
  isMobileLayout: boolean
}

const HistorySlider: FC<HistorySliderProps> = ({
  isProjectViewOnly,
  isFeatured,
  engineState,
  handleOnButtonClick,
  isCreatingProject,
  isDuplicatingProject,
  sliderRef,
  height,
  isMobileLayout
}) => {
  const t = useTranslations()
  const historySliderPosition = useHistoryState('historySliderPosition')
  const { setHistorySliderPosition } = useHistoryActions()

  const [historyPlaying, setHistoryPlaying] = useState<boolean>(false)

  const animationIntervalId = useRef<NodeJS.Timeout | null>(null)

  const haltSliderAnimation = () => {
    setHistoryPlaying(false)
    if (animationIntervalId.current) {
      clearInterval(animationIntervalId.current)
    }
  }

  const animateSlider = (initialPosition: number) => {
    let position = initialPosition

    animationIntervalId.current = setInterval(() => {
      if (position >= animationMax) {
        position = 0
        setHistorySliderPosition(Math.min(position, 1))
      } else {
        position = position + animationStep

        setHistorySliderPosition(position)
      }
    }, animationInterval)
  }

  const handlePlayButtonClick = () => {
    if (historyPlaying) {
      haltSliderAnimation()
      return
    }

    setHistoryPlaying(true)

    if (historySliderPosition >= 1) {
      animateSlider(0)
    } else {
      animateSlider(historySliderPosition)
    }
  }

  useEffect(() => {
    return () => {
      if (animationIntervalId.current) {
        clearInterval(animationIntervalId.current)
        setHistorySliderPosition(0)
      }
    }
  }, [])

  return (
    <div ref={sliderRef} className={styles['slider-controls']}>
      <div className={styles['slider-container']}>
        <OverlayTrigger placement="bottom" offset={0}>
          <ActionButton
            quiet
            slot="trigger"
            onClick={() => handlePlayButtonClick()}
          >
            <Icon slot="icon" size="l">
              {historyPlaying ? <PauseIcon /> : <PlayIcon />}
            </Icon>
          </ActionButton>

          <Tooltip slot="hover-content">
            {t('dashboard:historyDialog:tooltip')}
          </Tooltip>
        </OverlayTrigger>

        <Slider
          variant="filled"
          min={0}
          max={1}
          step={0.01}
          aria-label="History"
          labelVisibility="none"
          quiet
          style={{ height: height }}
          value={historySliderPosition}
          onInput={e => {
            const value = (e as unknown as { target: { value: number } }).target
              .value
            haltSliderAnimation()
            setHistorySliderPosition(value)
          }}
        />

        {!isMobileLayout && (isProjectViewOnly || isFeatured) && (
          <Button
            id={
              isProjectViewOnly
                ? 'create-new-design-btn'
                : HTMLElementIdsForTracking.duplicateRemixButton
            }
            disabled={engineState !== 'INITIALIZED_AND_DOCUMENT_LOADED'}
            onClick={handleOnButtonClick}
            variant="accent"
            slot="button"
            pending={isCreatingProject || isDuplicatingProject}
          >
            {isProjectViewOnly
              ? t('dashboard:historyDialog:create')
              : t('dashboard:historyDialog:duplicate')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default HistorySlider
